import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField,
} from '@mui/material'
import {
  dismissActionRoutine,
  selectMutationLoading,
} from 'features/actions/ducks'
import { promisifyRoutine } from 'redux-saga-routines'
import SubmitError from './SubmitError'
import { firstErrorMessage } from 'features/actions/ducks/messages'

const dismissAsync = promisifyRoutine(dismissActionRoutine)

const dialogPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}

const dialogTitleSx = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#0C1229',
  padding: '20px 24px',
  borderBottom: '1px solid #f2f2f2',
}

const dialogContentSx = {
  padding: '20px 24px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}

const helperTextSx = {
  fontSize: 13,
  color: '#475569',
  margin: 0,
  lineHeight: 1.5,
}

const dialogActionsSx = {
  padding: '16px 24px',
  borderTop: '1px solid #f2f2f2',
  gap: '8px',
}

const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  color: '#0C1229',
  borderColor: '#f2f2f2',
  borderRadius: '8px',
  padding: '8px 20px',
  minHeight: '40px',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    borderColor: '#0C1229',
  },
}

const dismissButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#0C1229',
  borderRadius: '8px',
  padding: '8px 24px',
  minHeight: '40px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#475569',
  },
}

const DismissActionDialog = ({ action, open, onClose, onDismissed, zIndex }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectMutationLoading)
  const [submitError, setSubmitError] = useState(null)
  useEffect(() => { if (open) setSubmitError(null) }, [open])
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) setReason('')
  }, [open])

  if (!action) return null

  const submit = async () => {
    setSubmitError(null)
    try {
      await dismissAsync({ id: action.id, reason: reason.trim() || undefined }, dispatch)
      if (onDismissed) onDismissed()
      else onClose?.()
    } catch (e) {
      setSubmitError(firstErrorMessage(e, 'This AI find was not removed. Please try again.'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
      sx={zIndex ? { zIndex } : undefined}
    >
      <DialogTitle sx={dialogTitleSx}>Remove an AI find that is not a real issue</DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <p style={helperTextSx}>
          Our AI found &ldquo;<strong>{action.title}</strong>&rdquo; in one of your documents. If
          it is not a real problem, you can remove it here and it will not become a remedial. You
          can add a short note saying why, so others can see the reason later.
        </p>
        <TextField
          label="Reason (optional)"
          multiline
          rows={3}
          fullWidth
          size="small"
          autoFocus
          placeholder="e.g. Does not apply to this building, or the document is out of date"
          value={reason}
          onChange={e => setReason(e.target.value)}
          data-tour-id='dismissDialog.reason'
        />
      </DialogContent>
      {submitError && <SubmitError role='alert'>{submitError}</SubmitError>}
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting}
          sx={dismissButtonSx}
          data-tour-id='dismissDialog.submit'
        >
          Not a real issue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DismissActionDialog
